import '@livekit/components-styles';
import {
  LiveKitRoom,
  AudioTrack,
  VideoTrack,
  useTracks,
  GridLayout,
  ParticipantTile,
  TrackRefContext,
  isTrackReference,
} from '@livekit/components-react';
import type { TrackReference } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { VolumeOff, VolumeUp } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { buildCroppedMap, selectByCroppedPreference } from '@/helpers/cropped.helper';
import { livekitIdentityMatchesPlayerId } from '@/helpers/livekit-identity.helper';
import { useHasSharedLiveKitRoom } from './shared-livekit-room.context';

export interface LiveKitTrackPreviewProps {
  /** LiveKit server URL. Omitted when an ancestor `SharedLiveKitRoomProvider` owns the connection. */
  serverUrl?: string;
  /** JWT token for room connection. Omitted when an ancestor owns the connection. */
  token?: string;
  /** Optional: filter to only these track SIDs. If empty, shows all subscribed tracks. */
  trackSids?: string[];
  /** Optional: only show tracks from this publisher (Mongo player id). When set, no room-wide grid. */
  publisherPlayerId?: string;
  /** Optional: allow screenshare. Default true. */
  showScreenshare?: boolean;
  /** Optional: allow camera. Default true. */
  showCamera?: boolean;
  /** Optional: allow microphone. Default true. */
  showMicrophone?: boolean;
  /** Callback when connection fails */
  onError?: (error: Error) => void;
  /** Custom class name for styling */
  className?: string;
  /** Height of the preview area */
  height?: string | number;
  /** When false, render video only (no ParticipantTile metadata / mute control). Default true. */
  showControls?: boolean;
}

/**
 * Joins a LiveKit room and renders selected track(s).
 * Reusable for: event editor preview, contributions view (users viewing remote speakers).
 */
export function LiveKitTrackPreview({
  serverUrl,
  token,
  trackSids = [],
  publisherPlayerId,
  showScreenshare = true,
  showCamera = true,
  showMicrophone = true,
  onError,
  className,
  height = '100%',
  showControls = true,
}: LiveKitTrackPreviewProps) {
  const hasSharedRoom = useHasSharedLiveKitRoom();

  const content = (
    <TrackPreviewContent
      trackSids={trackSids}
      publisherPlayerId={publisherPlayerId}
      showScreenshare={showScreenshare}
      showCamera={showCamera}
      showMicrophone={showMicrophone}
      showControls={showControls}
    />
  );

  const canConnectOwnRoom = Boolean(token && serverUrl);

  return (
    <Box sx={{ height, width: '100%', position: 'relative' }} className={className}>
      {hasSharedRoom || !canConnectOwnRoom ? (
        content
      ) : (
        <LiveKitRoom
          video={false}
          audio={false}
          connect={true}
          token={token!}
          serverUrl={serverUrl!}
          onError={onError}
          style={{ height: '100%', width: '100%' }}
        >
          {content}
        </LiveKitRoom>
      )}
    </Box>
  );
}

function filterVisualTracks(
  tracks: { publication?: { trackSid?: string }; source: Track.Source; participant: { isLocal: boolean } }[],
  authorizedSids: string[],
): TrackReference[] {
  return tracks.filter((t): t is TrackReference => {
    if (!t.publication?.trackSid) return false;
    if (t.source === Track.Source.Microphone || t.source === Track.Source.ScreenShareAudio) return false;
    if (authorizedSids.length > 0 && !authorizedSids.includes(t.publication.trackSid)) return false;
    return true;
  });
}

function filterAudioTracks(
  tracks: { publication?: { trackSid?: string }; source: Track.Source; participant: { isLocal: boolean } }[],
  authorizedSids: string[],
): TrackReference[] {
  return tracks.filter((t): t is TrackReference => {
    if (!t.publication?.trackSid) return false;
    if (t.participant.isLocal) return false;
    if (t.source !== Track.Source.Microphone && t.source !== Track.Source.ScreenShareAudio) return false;
    if (authorizedSids.length > 0 && !authorizedSids.includes(t.publication.trackSid)) return false;
    return true;
  });
}

function TrackPreviewContent({
  trackSids,
  publisherPlayerId,
  showScreenshare,
  showCamera,
  showMicrophone,
  showControls,
}: {
  trackSids: string[];
  publisherPlayerId?: string;
  showScreenshare: boolean;
  showCamera: boolean;
  showMicrophone: boolean;
  showControls: boolean;
}) {
  const [audioEnabled, setAudioEnabled] = useState(false);

  const sources = useMemo(() => {
    const s: { source: Track.Source; withPlaceholder: boolean }[] = [];
    if (showCamera) s.push({ source: Track.Source.Camera, withPlaceholder: false });
    if (showScreenshare) s.push({ source: Track.Source.ScreenShare, withPlaceholder: false });
    if (showMicrophone) s.push({ source: Track.Source.Microphone, withPlaceholder: false });
    s.push({ source: Track.Source.ScreenShareAudio, withPlaceholder: false });
    return s;
  }, [showCamera, showScreenshare, showMicrophone]);

  const allTracks = useTracks(sources, { onlySubscribed: false });

  const tracksForPreview = useMemo(() => {
    if (!publisherPlayerId) return allTracks;
    return allTracks.filter((t) => livekitIdentityMatchesPlayerId(t.participant.identity, publisherPlayerId));
  }, [allTracks, publisherPlayerId]);

  const filteredVideoTracks = useMemo(
    () =>
      trackSids.length > 0 ? filterVisualTracks(tracksForPreview, trackSids) : filterVisualTracks(tracksForPreview, []),
    [tracksForPreview, trackSids],
  );

  const croppedMap = useMemo(() => buildCroppedMap(filteredVideoTracks), [filteredVideoTracks]);
  const videoTracks = useMemo(
    () => selectByCroppedPreference(filteredVideoTracks, croppedMap, false),
    [filteredVideoTracks, croppedMap],
  );

  const audioTracks = useMemo(
    () =>
      trackSids.length > 0 ? filterAudioTracks(tracksForPreview, trackSids) : filterAudioTracks(tracksForPreview, []),
    [tracksForPreview, trackSids],
  );

  if (videoTracks.length === 0 && audioTracks.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#1a1a1a',
        }}
      >
        <CircularProgress size={28} aria-label="Loading preview" sx={{ color: 'rgba(255,255,255,0.8)' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#000',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {audioTracks.map((trackRef) => (
        <AudioTrack
          key={`${trackRef.participant.identity}-${trackRef.publication?.trackSid}`}
          trackRef={trackRef}
          muted={!audioEnabled}
          volume={audioEnabled ? 1 : 0}
        />
      ))}
      {videoTracks.length > 0 &&
        (publisherPlayerId ? (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#000',
            }}
          >
            {videoTracks.map((trackRef) => (
              <Box
                key={`${trackRef.participant.identity}-${trackRef.publication?.trackSid}`}
                sx={{ flex: 1, minHeight: 0, position: 'relative' }}
              >
                {showControls ? (
                  <ParticipantTile trackRef={trackRef} />
                ) : (
                  <VideoTrack trackRef={trackRef} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <GridLayout tracks={videoTracks} style={{ height: '100%', width: '100%', backgroundColor: '#000' }}>
            <TrackRefContext.Consumer>
              {(trackRef) =>
                trackRef && isTrackReference(trackRef) ? (
                  showControls ? (
                    <ParticipantTile trackRef={trackRef} />
                  ) : (
                    <VideoTrack trackRef={trackRef} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  )
                ) : null
              }
            </TrackRefContext.Consumer>
          </GridLayout>
        ))}

      {showControls && (audioTracks.length > 0 || videoTracks.length > 0) && (
        <Tooltip title={audioEnabled ? 'Mute audio' : 'Enable audio'}>
          <IconButton
            onClick={() => setAudioEnabled((prev) => !prev)}
            sx={{
              'position': 'absolute',
              'bottom': 12,
              'right': 12,
              'bgcolor': 'rgba(0,0,0,0.6)',
              'color': 'white',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.8)',
              },
            }}
            aria-label={audioEnabled ? 'Mute audio' : 'Enable audio'}
          >
            {audioEnabled ? <VolumeUp /> : <VolumeOff />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
